import axios from "axios";
import moment from "moment";
import { setAllAllowedRoutes } from "@/store/auth/authSlice";
import { toast } from "react-toastify";

export const mergeClass = (...classes) => {
  return classes.join(" ");
};

export const seperateWordByHyphen = (pageName) => {
  return pageName.replace(/-/g, " ");
};

export const getFormattedPrice = (price, currency = "R") => {
  return `${currency}${parseFloat(price).toFixed(2)}`;
};

export const postVideoToS3 = async ({ video, url, setVideoProgress }) => {
  await axios
    .put(url, video, {
      headers: {
        "Content-Type": video.type,
      },
      onUploadProgress: (data) => {
        setVideoProgress(Math.round((100 * data.loaded) / data.total));
      },
    })
    .then(() => {
      // toast.success("Video updated successfully.");
      return true;
    })
    .catch(async (e) => {
      toast.warn("Something went wrong. Please try again later.");
      return false;
    })
    .finally(() => {
      setVideoProgress(0);
    });
};

export const formattedKey = (key) => {
  return key
    ?.replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str?.toUpperCase());
};

export const formatDate = (dateString, formatType) => {
  if (!dateString) return "";

  // Check the formatType and format the dateString accordingly
  if (formatType === "YYYY-MM-DD") {
    return moment(dateString).format("YYYY-MM-DD");
  } else if (formatType === "long") {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(moment(dateString).startOf("day")).toLocaleDateString(
      undefined,
      options
    );
  }
};

export const getUniqueBrowserId = () => {
  const uniqueBrowserId = localStorage?.getItem("uniqueBrowserId");
  if (uniqueBrowserId) {
    return uniqueBrowserId;
  }

  if (window.navigator) {
    var navigator_info = window.navigator;
    var screen_info = window.screen;
    var uid = navigator_info.mimeTypes.length;
    uid += navigator_info.userAgent.replace(/\D+/g, "");
    uid += navigator_info.plugins.length;
    uid += screen_info.height || "";
    uid += screen_info.width || "";
    uid += screen_info.pixelDepth || "";

    localStorage.setItem("uniqueBrowserId", uid);
    return uid;
  } else {
    const theId =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    localStorage.setItem("uniqueBrowserId", theId);
    return theId;
  }
};

export const getFormattedLabel = (label = "") => {
  // replace all the special characters with space and then replace multiple spaces with single space and then trim the string
  return String(label)
    .replace(/[^a-zA-Z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

export const extractErrors = (errors) => {
  let errorMessage = "";

  Object.keys(errors).forEach((key) => {
    if (typeof errors[key] === "string") {
      // If it's a string, add it as an error message
      errorMessage += `➤ ${errors[key]}\n`;
    } else if (typeof errors[key] === "object" && errors[key] !== null) {
      // If it's an object, recurse
      errorMessage += extractErrors(errors[key]);
    }
  });

  return errorMessage;
};

export const flattenObjectKeys = (obj, prefix = "") => {
  return Object.keys(obj).reduce((acc, key) => {
    const fullKey = prefix ? `${prefix}.${key}` : key;
    if (
      typeof obj[key] === "object" &&
      !Array.isArray(obj[key]) &&
      obj[key] !== null
    ) {
      // Recursively flatten nested objects
      acc.push(...flattenObjectKeys(obj[key], fullKey));
    } else {
      acc.push(fullKey);
    }
    return acc;
  }, []);
};

export const handleUpdatedRoutes = ({
  sidebarData = [],
  allPermissions = [],
  dispatch,
}) => {
  const extractPagePaths = (route) => {
    if (route?.submenu) {
      return route.submenu.flatMap((subRoute) => extractPagePaths(subRoute));
    }
    return [route.pagePath];
  };
  let allowedRoutes = sidebarData.flatMap((route) => extractPagePaths(route));
  allowedRoutes.push("/dashboard");
  if (allPermissions.includes("web-user.edit")) {
    allowedRoutes.push("/user-management/web-user-list");
  }
  dispatch(setAllAllowedRoutes(allowedRoutes));
};

export const sanitizeNewlines = (str = "") => {
  return str.replace(/\r?\n/g, " ");
};

export const removeDuplicateSpaces = (str = "") => {
  return str.replace(/\s+/g, " ");
};

export const normalizeString = (str = "") => {
  return removeDuplicateSpaces(sanitizeNewlines(str.toString())).trim();
};

export const capitalizeEachWord = (input) => {
  return input
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const getNestedValue = (obj, key) => {
  return key.split(".").reduce((acc, part) => acc && acc[part], obj);
};

export const dropdownOptionsFormatter = (
  options,
  labelFormatter = null,
  valueFormatter = null
) => {
  if (!options || !Array.isArray(options)) return [];

  return options.map((item) =>
    formatDropdownItem(item, labelFormatter, valueFormatter)
  );
};

export const formatDropdownItem = (
  item,
  labelFormatter = null,
  valueFormatter = null
) => {
  if (!item) return null;

  return {
    label: labelFormatter
      ? labelFormatter(item)
      : `${item?.name ?? item?.title}`,
    value: valueFormatter ? valueFormatter(item) : item?.id ?? null,
    data: item,
  };
};

/**
 * Manually format a number with commas as thousands separator and dot as decimal separator.
 * @param {number} number - The number to format.
 * @param {boolean} isCurrency - If true, the number will be formatted as currency (e.g., $9,650.00).
 * @param {number} decimals - The number of decimals to keep.
 * @param {string} [currency='EUR'] - The currency code for currency formatting (e.g., 'EUR').
 * @returns {string} - The formatted number.
 */
export const formatNumber = (
  number,
  isCurrency = false,
  decimals = 4,
  currency = "EUR"
) => {
  if (isNaN(number)) {
    return "-";
  }

  let formattedNumber = Number(number).toFixed(decimals);

  let [integerPart, decimalPart] = formattedNumber.split(".");

  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  formattedNumber = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;

  if (isCurrency) {
    const currencySymbol = currency === "USD" ? "$" : "€";
    formattedNumber =
      currency === "EUR"
        ? `${formattedNumber} ${currencySymbol}`
        : `${currencySymbol} ${formattedNumber}`;
  }

  return formattedNumber;
};

export const formatDateTime = (
  dateTime,
  isDateOnly = false,
  isTimeOnly = false,
  isHourOnly = false
) => {
  if (!dateTime) return null;
  const [date, time] = dateTime.split("T");
  const [year, month, day] = date.split("-");
  const ymd = `${year}-${month}-${day}`;
  if (isDateOnly) return ymd;
  const [hours, minutes, seconds] = time.split(":");
  if (isHourOnly) return `${ymd} ${hours}:${minutes}`;
  if (isTimeOnly) return `${hours}:${minutes}:${seconds.slice(0, 2)}`;
  return `${ymd} | ${hours}:${minutes}:${seconds.slice(0, 2)}`;
};

export const truncate = (string = "", maxLength = 50) =>
  string?.length > maxLength ? `${string.slice(0, maxLength)}...` : string;

export const toTitleCase = (str = "") => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const diffForHumans = (dateTime) => {
  if (!dateTime) return null;

  const seconds = Math.round((new Date(dateTime) - Date.now()) / 1000);
  const units = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60,
    second: 1,
  };

  for (const [unit, value] of Object.entries(units)) {
    const diff = Math.round(seconds / value);
    if (Math.abs(diff) >= 1)
      return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(
        diff,
        unit
      );
  }

  return "just now";
};
